import { type ContributionTierId } from '@/lib/contribution'
import {
  getGaMeasurementId,
  isGaCollectionAllowed,
  logGaDiagnostic,
  whenGtagReady,
} from '@/lib/analytics/gaConfig'
import {
  sanitizeAnalyticsLocation,
  sanitizeAnalyticsPath,
} from '@/lib/analytics/sanitizeAnalyticsPath'
import {
  __resetWagamourAnalyticsPathForTests,
  remapAppBetaShellPageLocation,
  remapAppBetaShellPagePath,
  resolveAnalyticsPagePath,
  setCurrentWagamourAnalyticsScreen,
  type WagamourV2Screen,
} from '@/lib/analytics/wagamourScreenPath'

export type { WagamourV2Screen } from '@/lib/analytics/wagamourScreenPath'
export { __resetWagamourAnalyticsPathForTests } from '@/lib/analytics/wagamourScreenPath'

/**
 * Wagamour GA4 — single init (see components/GoogleAnalytics.tsx).
 * Measurement ID: NEXT_PUBLIC_GA_MEASUREMENT_ID only (see lib/analytics/gaConfig.ts).
 */

export { getGaMeasurementId } from '@/lib/analytics/gaConfig'

declare global {
  interface Window {
    gtag?: (
      command: 'config' | 'event' | 'set' | 'js',
      targetId: string | Date | Record<string, any>,
      config?: Record<string, any>
    ) => void
    dataLayer?: any[]
  }
}

/** Canonical analytics path: V2 screen when on `/app-beta`, else sanitized pathname. */
export function getAnalyticsPagePath(): string {
  if (typeof window === 'undefined') return ''
  return resolveAnalyticsPagePath(window.location.pathname || '')
}

function pagePath(): string {
  return getAnalyticsPagePath()
}

function sanitizeAnalyticsEventParams(
  params?: Record<string, any>
): Record<string, any> {
  const next = { ...(params || {}) }
  if (typeof next.page_path === 'string') {
    next.page_path = remapAppBetaShellPagePath(next.page_path)
  }
  if (typeof next.page_location === 'string') {
    next.page_location = remapAppBetaShellPageLocation(next.page_location)
  }
  return next
}

/** Merge automatic page_path for product/marketing helpers (optional for callers). */
function withPage(params?: Record<string, any>): Record<string, any> {
  return sanitizeAnalyticsEventParams({
    page_path: pagePath(),
    ...(params || {}),
  })
}

/**
 * Raw GA4 event — no-op if collection disallowed or gtag missing.
 */
export function trackEvent(name: string, params?: Record<string, any>): void {
  if (typeof window === 'undefined') return
  if (!isGaCollectionAllowed()) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics] Event (blocked):', name, params)
    }
    return
  }
  if (!window.gtag) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics] Event (not tracked):', name, params)
    }
    return
  }
  window.gtag('event', name, sanitizeAnalyticsEventParams(params))
}

/**
 * Associate GA user_id with the current client (no-op if gtag or ID missing).
 */
const ANALYTICS_USER_ID_STORAGE = 'wagamour_analytics_user_id'

export function setGaUserId(userId: string | null | undefined): void {
  try {
    if (typeof window !== 'undefined') {
      if (userId) sessionStorage.setItem(ANALYTICS_USER_ID_STORAGE, userId)
      else sessionStorage.removeItem(ANALYTICS_USER_ID_STORAGE)
    }
  } catch {
    /* ignore */
  }
  if (typeof window === 'undefined' || !window.gtag) return
  if (!isGaCollectionAllowed()) return
  const measurementId = getGaMeasurementId()
  if (!measurementId) return
  if (userId) {
    window.gtag('config', measurementId, {
      user_id: userId,
      send_page_view: false,
    })
  } else {
    // Explicitly clear User-ID; do not send a page view.
    window.gtag('config', measurementId, {
      user_id: null,
      send_page_view: false,
    })
  }
}

/** @deprecated Prefer `sign_up` only — kept to avoid churn at call sites; does not emit (duplicate of sign_up). */
export function trackSignupComplete(_method: 'email' | 'google' | 'apple' = 'email') {
  // Intentionally empty: sign_up is fired at the same action point.
}

/** Funnel: marketing hero primary CTAs */
export function trackHeroCtaClick(ctaId: string) {
  trackEvent(
    'hero_cta_click',
    withPage({
      event_category: 'marketing',
      event_label: ctaId,
      cta_id: ctaId,
    })
  )
}

/** Funnel: use-case discovery cards */
export function trackUseCaseClick(href: string, title: string) {
  trackEvent(
    'use_case_click',
    withPage({
      event_category: 'marketing',
      event_label: title,
      use_case_href: href,
      use_case_title: title,
    })
  )
}

const APP_ENTER_KEY = 'wagamour_ga_app_enter'

/** In-memory guard — prevents overlapping readiness polls for app_enter. */
let appEnterInFlight = false
let appEnterAbort: AbortController | null = null

/** Bounded gtag wait for app_enter / app_opened_mode — longer than first paint. */
export const APP_ENTER_GTAG_TIMEOUT_MS = 12_000
const APP_ENTER_GTAG_INTERVAL_MS = 50

/** Test-only reset for app_enter in-flight / abort state. */
export function __resetTrackAppEnterForTests(): void {
  appEnterInFlight = false
  if (appEnterAbort) {
    appEnterAbort.abort()
    appEnterAbort = null
  }
  __resetWagamourAnalyticsPathForTests()
}

/**
 * First entry to /app-beta per browser tab session.
 * Session dedupe marker is set only after app_enter is successfully queued to
 * window.gtag (not confirmed network delivery).
 */
export function trackAppEnter(source: 'pwa' | 'web') {
  if (typeof window === 'undefined') return
  if (!isGaCollectionAllowed()) {
    logGaDiagnostic('app_enter_blocked', { reason: 'collection_disallowed', source })
    return
  }

  try {
    if (sessionStorage.getItem(APP_ENTER_KEY)) return
  } catch {
    // sessionStorage may be blocked — still attempt emit once
  }

  if (appEnterInFlight) return
  appEnterInFlight = true

  if (appEnterAbort) {
    appEnterAbort.abort()
  }
  const controller = new AbortController()
  appEnterAbort = controller

  void (async () => {
    try {
      const ready = await whenGtagReady({
        timeoutMs: APP_ENTER_GTAG_TIMEOUT_MS,
        intervalMs: APP_ENTER_GTAG_INTERVAL_MS,
        signal: controller.signal,
      })
      if (!ready || controller.signal.aborted) {
        logGaDiagnostic('app_enter_gtag_not_ready', { source })
        return
      }
      if (!isGaCollectionAllowed() || typeof window.gtag !== 'function') {
        logGaDiagnostic('app_enter_blocked', {
          reason: 'post_ready_disallowed',
          source,
        })
        return
      }

      try {
        if (sessionStorage.getItem(APP_ENTER_KEY)) return
      } catch {
        /* ignore */
      }

      const params = withPage({
        event_category: 'engagement',
        event_label: source,
        entry_source: source,
      })
      window.gtag('event', 'app_enter', sanitizeAnalyticsEventParams(params))

      // Successfully queued to gtag — mark session so we do not re-queue.
      try {
        sessionStorage.setItem(APP_ENTER_KEY, '1')
      } catch {
        /* ignore */
      }
      logGaDiagnostic('app_enter_queued', { source })
    } finally {
      if (appEnterAbort === controller) {
        appEnterAbort = null
      }
      appEnterInFlight = false
    }
  })()
}

export type CoreActionType = 'park_loaded' | 'play_viewed' | 'location_granted'

const CORE_KEYS: Record<CoreActionType, string> = {
  park_loaded: 'wagamour_ga_core_park_loaded',
  play_viewed: 'wagamour_ga_core_play',
  location_granted: 'wagamour_ga_core_location',
}

/** Core product actions — once per tab session per action type */
export function trackCoreAction(
  action: CoreActionType,
  extra?: { label?: string; [key: string]: unknown }
) {
  if (typeof window === 'undefined') return
  const key = CORE_KEYS[action]
  try {
    if (sessionStorage.getItem(key)) return
    sessionStorage.setItem(key, '1')
  } catch {
    // ignore
  }
  const label = extra?.label ?? action
  const { label: _x, ...params } = extra || {}
  trackEvent(
    'core_action',
    withPage({
      event_category: 'product',
      event_label: label,
      action_type: action,
      ...params,
    })
  )
}

export function trackPWAInstall(platform: 'ios' | 'android' | 'desktop') {
  trackEvent(
    'pwa_installed',
    withPage({
      event_category: 'engagement',
      event_label: platform,
      platform,
      value: 1,
    })
  )
}

export function trackPWAInstallPrompt(platform: string) {
  trackEvent(
    'install_prompt_shown',
    withPage({
      event_category: 'engagement',
      event_label: platform,
      platform,
    })
  )
}

export function trackPWAInstallDismissed(platform: string) {
  trackEvent(
    'pwa_install_dismissed',
    withPage({
      event_category: 'engagement',
      event_label: platform,
      platform,
    })
  )
}

/** @deprecated Prefer trackAppEnter for /app-beta funnel */
export function trackAppOpen(source?: string) {
  trackEvent(
    'app_open',
    withPage({
      event_category: 'engagement',
      event_label: source || 'direct',
      source: source || 'direct',
    })
  )
}

export function trackParkCheckIn(parkId: string, parkName: string) {
  trackEvent(
    'park_checkin_submitted',
    withPage({
      event_category: 'feature_usage',
      event_label: parkName,
      park_id: parkId,
      park_name: parkName,
      value: 1,
    })
  )
}

export function trackAIChat(messageLength: number, hasVoice: boolean = false) {
  trackEvent(
    'ai_chat_message',
    withPage({
      event_category: 'feature_usage',
      event_label: hasVoice ? 'voice' : 'text',
      message_length: messageLength,
      input_method: hasVoice ? 'voice' : 'text',
      value: 1,
    })
  )
}

export function trackPhotoUpload(parkId: string, parkName: string) {
  trackEvent(
    'park_photo_uploaded',
    withPage({
      event_category: 'feature_usage',
      event_label: parkName,
      park_id: parkId,
      park_name: parkName,
      value: 1,
    })
  )
}

export function trackParkDiscovery(parksFound: number) {
  trackEvent(
    'park_discovery',
    withPage({
      event_category: 'feature_usage',
      event_label: `${parksFound} parks`,
      parks_found: parksFound,
      value: parksFound,
    })
  )
  if (parksFound > 0) {
    trackCoreAction('park_loaded', {
      label: 'park_list',
      parks_found: parksFound,
    })
  }
}

export function trackContributionScoreUpdated(
  score: number,
  delta: number,
  source: string
) {
  trackEvent(
    'contribution_score_updated',
    withPage({
      event_category: 'community',
      event_label: source,
      score,
      delta,
      source,
    })
  )
}

export function trackContributionTierChanged(
  tierId: ContributionTierId,
  score: number
) {
  trackEvent(
    'contribution_tier_changed',
    withPage({
      event_category: 'community',
      event_label: tierId,
      tier: tierId,
      score,
    })
  )
}

export function trackParkUpdateCreated(
  parkId: string,
  parkName: string,
  updateType: 'status' | 'busyness'
) {
  trackEvent(
    'park_update_created',
    withPage({
      event_category: 'community',
      event_label: `${updateType}_${parkName}`,
      park_id: parkId,
      park_name: parkName,
      update_type: updateType,
    })
  )
}

export function trackPageView(pagePathArg: string, pageTitle?: string) {
  if (typeof window === 'undefined' || !window.gtag) return
  if (!isGaCollectionAllowed()) return
  const measurementId = getGaMeasurementId()
  if (!measurementId) return

  const safePath = sanitizeAnalyticsPath(pagePathArg)
  const origin =
    typeof window.location?.origin === 'string' && window.location.origin
      ? window.location.origin
      : ''
  const safeLocation = origin
    ? sanitizeAnalyticsLocation(`${origin}${safePath}`)
    : sanitizeAnalyticsLocation(safePath)

  window.gtag('config', measurementId, {
    page_path: safePath,
    page_location: safeLocation,
    page_title: pageTitle,
    send_page_view: true,
  })
}

const WAGAMOUR_V2_SCREEN_TITLES: Record<WagamourV2Screen, string> = {
  home: 'Home',
  parks: 'Parks',
  play: 'Play',
  picks: 'Picks',
  profile: 'Profile',
}

/** V2 SPA virtual screen — GA4 screen_view + virtual page_path under /app-beta/{screen}. */
export function trackWagamourScreen(screen: WagamourV2Screen): void {
  const pagePath = setCurrentWagamourAnalyticsScreen(screen)
  const screenTitle = WAGAMOUR_V2_SCREEN_TITLES[screen]
  const origin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : ''
  const pageLocation = origin
    ? sanitizeAnalyticsLocation(`${origin}${pagePath}`)
    : pagePath

  if (process.env.NODE_ENV === 'development') {
    console.log('[Analytics] screen_view', screen)
  }

  trackEvent('screen_view', {
    screen_name: screen,
    wagamour_screen: screen,
    app_surface: 'app_beta',
    page_path: pagePath,
    page_location: pageLocation,
    event_category: 'navigation',
    event_label: screen,
  })

  trackPageView(pagePath, `Wagamour ${screenTitle}`)
}

export function trackSignUp(method: 'email' | 'google' | 'apple') {
  trackEvent(
    'sign_up',
    withPage({
      event_category: 'user',
      event_label: method,
      method,
      value: 1,
    })
  )
}

export function trackLogin(method: 'email' | 'google' | 'apple') {
  trackEvent(
    'login',
    withPage({
      event_category: 'user',
      event_label: method,
      method,
      value: 1,
    })
  )
}
